import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import CTASection from "@/components/CTASection";
import { IMAGES } from "@/config/images";

const STAGES = [
  ["Design & Engineering", "Products are engineered from the application requirement — airflow, containment, transfer, cleaning and integration — before a single sheet is cut."],
  ["Material Selection", "SS 304 stainless steel as the standard construction grade; gaskets, glass and hardware chosen for cleanroom compatibility."],
  ["Fabrication", "Laser cutting and CNC forming to drawing tolerances, with fit-up checked before welding."],
  ["Welding", "Smooth, continuous TIG and laser welds that minimize crevices and support cleanability."],
  ["Finishing", "Surface finishes matched to the application — from matt industrial finishes to finer finishes for sterile areas."],
  ["Assembly", "Interlocks, blowers, filtration, controls and sensors integrated and function-checked as complete units."],
  ["Inspection", "Dimensional checks, weld and finish review, and functional verification before dispatch."],
  ["Testing", "Functional testing of interlocks, airflow and controls as applicable to the product configuration."],
  ["Documentation", "Drawings, material details and test records compiled to support your project documentation needs."],
  ["Customization", "Dimensions, materials, finishes and configurations adapted to project specifications."],
];

export default function Quality() {
  return (
    <div data-testid="quality-page">
      <SEO title="Quality & Engineering" path="/quality" image={IMAGES.micro}
        description="STJ Innovations' engineering approach: design, material selection, fabrication, welding, finishing, assembly, inspection, testing, documentation and customization." />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Breadcrumbs dark items={[{ label: "Quality & Engineering" }]} />
          <p className="eyebrow eyebrow-light">Quality & Engineering</p>
          <KineticLines lines={["Quality by Design"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            Quality is treated as a design input at STJ — engineered into material choices, fabrication methods and inspection at every stage.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Our Approach" title="Ten Disciplines, One Standard"
              copy="From first sketch to final inspection, every STJ product moves through a consistent engineering discipline." />
            <Reveal delay={0.15}>
              <div className="spotlight-img mt-10 overflow-hidden border border-line">
                <img src={IMAGES.lab} alt="Inspection and testing of cleanroom equipment" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="grid gap-px border border-line bg-line sm:grid-cols-2">
              {STAGES.map(([title, desc], i) => (
                <Reveal key={title} delay={(i % 2) * 0.06} className="bg-white">
                  <div className="h-full p-7" data-testid={`quality-stage-${i}`}>
                    <p className="font-mono text-[11px] tracking-[0.2em] text-accent">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-3 font-display text-lg font-semibold text-ink">{title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-steel">{desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="blueprint-dark bg-navy">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <SectionHead dark eyebrow="Engineering for Application" title="Built to Your Specification"
              copy="STJ solutions can be developed according to customer requirements and project specifications — dimensions, materials, finishes, interlock logic, filtration configuration and documentation. Share your requirement and our engineering team will respond with a practical proposal." />
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
